import React, { useState } from 'react';
import { formatPlatformName } from '../../utils/platformHelpers';
import { useQuery } from '@tanstack/react-query';
import { 
  ExternalLink, 
  Play, 
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2
} from 'lucide-react';
import { reportsAPI } from '../../api/reports';
import {
  formatNumber,
  formatPercent,
  formatDate,
  formatDuration,
  getPlatformColor,
  truncateText
} from '../../utils/formatters';
import YouTubeEstimateTooltip from '../YouTubeEstimateTooltip';

const ContentTable = ({ campaignId, dateRange }) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('postDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    platform: '',
    contentType: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-content', campaignId, page, sortBy, sortOrder, filters, dateRange],
    queryFn: () => reportsAPI.getCampaignContent(campaignId, {
      page,
      limit,
      sortBy,
      sortOrder,
      startDate: dateRange?.startDate?.toISOString(),
      endDate: dateRange?.endDate?.toISOString(),
      platform: filters.platform || undefined,
      contentType: filters.contentType || undefined
    }).then(r => r.data),
    enabled: !!campaignId
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const getContentTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'stream':
      case 'video':
        return <Play className="w-4 h-4" />;
      default:
        return <ImageIcon className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-sc-pink mr-2" />
        <span className="text-gray-600">Loading content...</span>
      </div>
    );
  }

  const content = data?.content || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };

  // Debug logging
  if (content.length > 0) {
    console.log('📊 ContentTable - First item data:', JSON.stringify(content[0], null, 2));
    console.log('📊 VOD Views value for first item:', content[0].metrics?.vodViews);
  }

  return (
    <div className="card-pink-accent bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header with filters */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Content ({pagination.total} items)
          </h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              showFilters ? 'bg-sc-pink/20 text-sc-pink' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>
        
        {/* Filter panel */}
        {showFilters && (
          <div className="mt-4 flex items-center space-x-4">
            <select
              value={filters.platform}
              onChange={(e) => {
                setFilters({ ...filters, platform: e.target.value });
                setPage(1);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
            >
              <option value="">All Platforms</option>
              <option value="twitch">Twitch</option>
              <option value="kick">Kick</option>
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
              <option value="twitter">Twitter</option>
              <option value="instagram">Instagram</option>
            </select>
            
            <select
              value={filters.contentType}
              onChange={(e) => {
                setFilters({ ...filters, contentType: e.target.value });
                setPage(1);
              }}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
            >
              <option value="">All Types</option>
              <option value="stream">Streams</option>
              <option value="vod">Stream VODs</option>
              <option value="video">Videos</option>
              <option value="post">Posts</option>
              <option value="short">Shorts/Reels</option>
            </select>
            
            {(filters.platform || filters.contentType) && (
              <button
                onClick={() => {
                  setFilters({ platform: '', contentType: '' });
                  setPage(1);
                }}
                className="text-sm text-sc-pink hover:text-sc-pink/80"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Content
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Creator
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('postDate')}
              >
                Date {sortBy === 'postDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('views')}
              >
                Views {sortBy === 'views' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                VOD Views
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center justify-end gap-1.5">
                  <span>Engagement</span>
                  <YouTubeEstimateTooltip metric="engagement" />
                </div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avg CCV
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Peak CCV
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Link
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {content.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {item.thumbnailUrl ? (
                      <img 
                        src={item.thumbnailUrl} 
                        alt={item.title}
                        className="w-16 h-10 object-cover rounded mr-3"
                      />
                    ) : (
                      <div className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center mr-3">
                        {getContentTypeIcon(item.contentType)}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {truncateText(item.title || 'Untitled', 40)}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: getPlatformColor(item.platform) + '20',
                            color: getPlatformColor(item.platform)
                          }}
                        >
                          {formatPlatformName(item.platform)}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">
                          {item.contentType}
                        </span>
                        {item.metrics.streamDuration && (
                          <span className="text-xs text-gray-500">
                            {formatDuration(item.metrics.streamDuration)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {item.creator?.profileImageUrl ? (
                      <img 
                        src={item.creator.profileImageUrl}
                        alt={item.creator.name}
                        className="w-8 h-8 rounded-full mr-2"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 mr-2" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.creator?.name}</p>
                      <p className="text-xs text-gray-500">@{item.creator?.handle}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {formatDate(item.postDate, 'short')}
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="font-medium text-gray-900">{formatNumber(item.metrics.views)}</p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="font-medium text-gray-900">
                    {item.platform?.toLowerCase() === 'twitch'
                      ? formatNumber(item.metrics.views)
                      : formatNumber(item.metrics.vodViews || (item.platform?.toLowerCase() === 'kick' ? item.metrics.views : 0) || 0)}
                  </p>
                </td>
                <td className="px-4 py-4 text-right">
                  <p className="text-sm text-gray-900">
                    {formatNumber(item.metrics.likes + item.metrics.comments + item.metrics.shares)}
                  </p>
                  <p className={`text-xs ${item.metrics.engagementRate > 3 ? 'text-green-600' : 'text-gray-500'}`}>
                    {formatPercent(item.metrics.engagementRate)}
                  </p>
                </td>
                <td className="px-4 py-4 text-right text-sm text-gray-600">
                  {item.metrics.avgCcv > 0 ? formatNumber(item.metrics.avgCcv) : '-'}
                </td>
                <td className="px-4 py-4 text-right text-sm text-gray-600">
                  {item.metrics.peakCcv > 0 ? formatNumber(item.metrics.peakCcv) : '-'}
                </td>
                <td className="px-4 py-4 text-center">
                  <a
                    href={item.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sc-pink hover:text-sc-pink/80"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {((pagination.page - 1) * limit) + 1} - {Math.min(pagination.page * limit, pagination.total)} of {pagination.total}
        </p>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className={`p-2 rounded-lg transition-colors ${
              page === 1 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    page === pageNum
                      ? 'bg-sc-pink text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === pagination.totalPages}
            className={`p-2 rounded-lg transition-colors ${
              page === pagination.totalPages 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentTable;
